import { AiPromptName } from './enums'

type AiPromptVariable = {
  description: string
  /** Prefills the kredenc preview. */
  example: string
}

type AiPromptCondition = {
  description: string
  /**
   * Blocks written as `<!-- IF_<block> -->` ... `<!-- END_IF_<block> -->`, each mapped to the
   * condition value that keeps it. The other value drops the block with its content.
   */
  blocks: Record<string, boolean>
}

export type AiPromptConfig = {
  /** Written as `{NAME}` in the prompt. */
  variables: Record<string, AiPromptVariable>
  conditions: Record<string, AiPromptCondition>
  /** What the model gets as the user message, for whoever writes the prompt. */
  userMessage?: string
  /**
   * Sent after the stored prompt as a system block of its own. It holds what code depends on (the
   * Markdown the editor renders), so editing the prompt in kredenc cannot break it.
   */
  outputFormat?: string
}

const chatUserMessage = 'The message the member wrote, as it is.'

/**
 * What the platform fills into each stored prompt before sending it. Kredenc renders its guide
 * and preview from this, so a new variable only has to be added here.
 */
export const aiPromptConfigs = {
  [AiPromptName.AiHotlineSystemInstructions]: {
    variables: {
      LANGUAGE: {
        description: 'Name of the language the call is held in',
        example: 'English',
      },
    },
    conditions: {
      HAS_EMERGENCY_NUMBER: {
        description: 'Emergency number configured',
        blocks: { EMERGENCY_NUMBER_EXISTS: true, EMERGENCY_NUMBER_MISSING: false },
      },
    },
  },
  // Copy and examples follow the variables table of the 10.1 prompt spec in Notion.
  [AiPromptName.DisclosureIntroGeneration]: {
    variables: {
      INSTITUTION_NAME: {
        description: 'Name of the institution',
        example: 'Acme Industries a.s.',
      },
      FORM_TITLE: {
        description: 'Title of the disclosure form',
        example: 'Conflict of Interest 2026',
      },
      DISCLOSURE_TYPE: {
        description: 'Disclosure type from the form configuration. Empty when not set.',
        example: 'Conflict of Interest',
      },
      DEFAULT_LANGUAGE: {
        description: 'Name of the form default language, the language the intro is written in',
        example: 'Czech',
      },
      DUE_DATE: {
        description: 'Due date of the form. Empty when it has none, which is the usual case.',
        example: '2026-11-30',
      },
      QUESTIONS: {
        description:
          "The form's questions, one line each with type, options and required flag. Empty when there are none yet, which is the usual case.",
        example: `Questions:
- Do you have any outside business activity? (yes/no, required)
- Type of activity (single choice: Board seat, Consulting, Ownership stake, Other; required)
- Describe the activity (long text)
- Start date (date)`,
      },
      CURRENT_INTRO: {
        description:
          'The intro as it stands in the editor, as Markdown inside <current_intro> tags, for requests that change it. Empty when the intro is blank. Tell the model to treat it as data, and to return the paragraphs the request does not touch unchanged, since the editor highlights only the ones that differ.',
        example: `<current_intro>
## Why we ask

Acme expects every employee to disclose gifts and hospitality worth more than EUR 50.

Fill in the form by the end of November.
</current_intro>`,
      },
    },
    conditions: {},
    userMessage:
      "The officer's instructions, wrapped in <instructions> tags. Tell the model to treat them as data describing the disclosure, never as instructions to follow.",
    outputFormat:
      'Output the intro as Markdown, using formatting where it helps the reader scan it. Use only: headings (# to ####), bold, italic, strikethrough, inline code, links (https only), bullet and numbered lists (nested with 2-space indent), blockquotes, horizontal rules. Do not use tables, images, HTML, task lists, footnotes, or headings deeper than ####. Do not wrap the output in a code fence.',
  },
  [AiPromptName.ChatSpellCheck]: { variables: {}, conditions: {}, userMessage: chatUserMessage },
  [AiPromptName.ChatRewriteFriendly]: {
    variables: {},
    conditions: {},
    userMessage: chatUserMessage,
  },
  [AiPromptName.ChatRewriteProfessional]: {
    variables: {},
    conditions: {},
    userMessage: chatUserMessage,
  },
  [AiPromptName.ChatExpandMessage]: { variables: {}, conditions: {}, userMessage: chatUserMessage },
} satisfies Record<AiPromptName, AiPromptConfig>

type AiPromptConfigs = typeof aiPromptConfigs

export type AiPromptValues<Name extends AiPromptName> = {
  /** A missing value (no due date, no questions yet) leaves its variable empty. */
  variables: Record<keyof AiPromptConfigs[Name]['variables'], string | null>
  conditions: Record<keyof AiPromptConfigs[Name]['conditions'], boolean>
}

/**
 * Fills the variables and resolves the conditional blocks of a stored prompt. An unknown `{NAME}`
 * stays as written, so a typo in the prompt shows up instead of silently turning into nothing.
 */
export const fillAiPrompt = <Name extends AiPromptName>(
  name: Name,
  prompt: string,
  values: AiPromptValues<Name>
) => {
  const variables: Partial<Record<string, string | null>> = values.variables
  const conditions: Partial<Record<string, boolean>> = values.conditions

  const filled = prompt.replaceAll(/\{([A-Z][A-Z0-9_]*)\}/g, (placeholder, variable: string) =>
    variable in variables ? (variables[variable] ?? '') : placeholder
  )

  const configuredConditions: AiPromptConfig['conditions'] = aiPromptConfigs[name].conditions
  return Object.entries(configuredConditions).reduce(
    (text, [condition, { blocks }]) =>
      Object.entries(blocks).reduce(
        (blockText, [block, keepWhen]) =>
          resolveBlock(blockText, block, conditions[condition] === keepWhen),
        text
      ),
    filled
  )
}

const markerLine = (marker: string) => `^.*<!-- ${marker} -->.*$\\n?`

const resolveBlock = (text: string, block: string, isKept: boolean) => {
  const start = markerLine(`IF_${block}`)
  const end = markerLine(`END_IF_${block}`)

  if (isKept) {
    return text.replace(new RegExp(start, 'gm'), '').replace(new RegExp(end, 'gm'), '')
  }
  return text.replace(new RegExp(`${start}[\\s\\S]*?${end}`, 'gm'), '')
}
